import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { resendVerificationEmail, selectAuthLoading, selectAuthError, selectVerificationMessage, selectRegistrationEmail, clearError } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '../contexts/ThemeContext';
import './Auth.css';

const ResendVerification: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const verificationMessage = useAppSelector(selectVerificationMessage);
  const registrationEmail = useAppSelector(selectRegistrationEmail);
  const { theme } = useTheme();

  const logoLarge = theme === 'dark'
    ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark.png`
    : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant.png`;

  // Pre-populate email if available from registration
  React.useEffect(() => {
    if (registrationEmail && !email) {
      setEmail(registrationEmail);
    }
  }, [registrationEmail, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    try {
      dispatch(clearError());
      const result = await dispatch(resendVerificationEmail({ email: email.trim() }));

      if (resendVerificationEmail.fulfilled.match(result)) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Resend verification failed:', error);
    }
  };

  if (emailSent) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src={logoLarge}
              alt="Debriefr"
              style={{ width: '200px', maxWidth: '100%' }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📧</div>
            <h1>Verification Email Sent</h1>

            <div className="alert alert-success" style={{ margin: '20px 0' }}>
              {verificationMessage || 'A new verification email has been sent to your inbox.'}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p>Please check your email and click the verification link to activate your account.</p>
              <p><small>Can't find the email? Check your spam folder.</small></p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <button
                onClick={() => setEmailSent(false)}
                className="btn btn-secondary"
                style={{ marginRight: '10px' }}
              >
                Send Another Email
              </button>
              <Link to="/login" className="btn btn-primary">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img
            src={logoLarge}
            alt="Debriefr"
            style={{ width: '200px', maxWidth: '100%' }}
          />
        </div>
        <h1>Resend Verification Email</h1>
        <p>Enter your email address to receive a new verification link.</p>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="Enter your email address"
              required
            />
            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
              We'll send a verification link to this email address
            </small>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={isLoading || !email.trim()}>
            {isLoading ? <LoadingSpinner size="sm" color="white" /> : 'Send Verification Email'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Remember your password? <Link to="/login">Sign in</Link></p>
          <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;